import type {
  PanelDesign,
  PanelGeometrySpec,
  PanelMesh,
  PanelRef,
  ShapeTessellation,
  TriangleMesh,
  Vec2,
  WallMesh,
} from '../types';
import type { HeightField } from '../heightfield';
import { createHeightFieldForDesign, edgeFalloff } from '../heightfield';
import { tessellateShape, type ShapeDefinition } from '../shapes/registry';
import { layoutForDesign, type WallLayout } from '../wall';
import { buildBacklight } from '../led/backlight';
import { createChannelField, type ChannelField } from '../led/channel';

/**
 * Panel mesh construction.
 *
 * Every panel is a closed solid: a displaced top surface, a flat bottom and a
 * vertical skirt joining the two. The skirt reuses the tessellation's boundary
 * loop, so the top surface and the walls share their outline vertices exactly
 * and the result is watertight — which is what slicers require and what the
 * test suite checks.
 *
 * The mesh is emitted in panel space with the back face on `z = 0`, so an
 * exported part already sits flat on the print bed.
 *
 * Backlighting does not touch any of this. The LED strip lives in a separate
 * frame behind the panel, so a backlit panel is the same solid as an unlit one.
 */

export interface BuildPanelOptions {
  shape: ShapeDefinition;
  tessellation: ShapeTessellation;
  outline: readonly Vec2[];
  field: HeightField;
  spec: PanelGeometrySpec;
  /** Set when a strip channel is cut into the back face. */
  channel?: ChannelField | null;
}

export function buildPanelMesh(panel: PanelRef, options: BuildPanelOptions): PanelMesh {
  const { tessellation, outline, field, spec } = options;
  const { positions: flat, triangles, boundary } = tessellation;

  // The mesh stays in the panel's own frame so the part prints in its canonical
  // orientation; only the sampling position is rotated into wall space. Hoisted
  // out of the loop because the panel's rotation is fixed for the whole mesh.
  const angle = (panel.rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const vertexCount = flat.length / 2;
  const ringCount = boundary.length;
  const channel = options.channel?.active ? options.channel : null;

  // A flat back needs only its outline plus a centre point, which is far
  // cheaper than a second full surface. A channelled back has to carry the
  // same tessellation as the top, so that cost is paid only when it is used.
  //
  // Layout: [ top surface | back ]  where the back is either the boundary ring
  // plus a centre, or a mirror of the top surface.
  const bottomVertexCount = channel ? vertexCount : ringCount + 1;
  const positions = new Float32Array((vertexCount + bottomVertexCount) * 3);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i < vertexCount; i += 1) {
    const lx = flat[i * 2];
    const ly = flat[i * 2 + 1];

    const normalised = field.sample(
      panel.centerX + lx * cos - ly * sin,
      panel.centerY + lx * sin + ly * cos,
      panel,
    );
    const falloff = edgeFalloff(lx, ly, outline, spec.edge);
    const z = spec.baseThickness + normalised * spec.reliefDepth * falloff;

    const o = i * 3;
    positions[o] = lx;
    positions[o + 1] = ly;
    positions[o + 2] = z;

    if (lx < minX) minX = lx;
    if (ly < minY) minY = ly;
    if (lx > maxX) maxX = lx;
    if (ly > maxY) maxY = ly;
    if (z > maxZ) maxZ = z;
  }

  const bottomStart = vertexCount;
  let indices: Uint32Array;
  let cursor: number;

  if (channel) {
    // The back mirrors the top surface, displaced upward where a run passes.
    for (let i = 0; i < vertexCount; i += 1) {
      const lx = flat[i * 2];
      const ly = flat[i * 2 + 1];
      const o = (bottomStart + i) * 3;
      positions[o] = lx;
      positions[o + 1] = ly;
      positions[o + 2] = channel.backHeight(
        panel.centerX + lx * cos - ly * sin,
        panel.centerY + lx * sin + ly * cos,
        lx,
        ly,
      );
    }

    indices = new Uint32Array(triangles.length * 2 + ringCount * 6);
    indices.set(triangles, 0);
    cursor = triangles.length;

    // The same triangulation, wound the other way so its normals point down.
    for (let i = 0; i < triangles.length; i += 3) {
      indices[cursor++] = bottomStart + triangles[i];
      indices[cursor++] = bottomStart + triangles[i + 2];
      indices[cursor++] = bottomStart + triangles[i + 1];
    }
  } else {
    let centroidX = 0;
    let centroidY = 0;
    for (let i = 0; i < ringCount; i += 1) {
      const source = boundary[i] * 2;
      const x = flat[source];
      const y = flat[source + 1];
      const o = (bottomStart + i) * 3;
      positions[o] = x;
      positions[o + 1] = y;
      positions[o + 2] = 0;
      centroidX += x;
      centroidY += y;
    }

    const centreIndex = bottomStart + ringCount;
    positions[centreIndex * 3] = centroidX / ringCount;
    positions[centreIndex * 3 + 1] = centroidY / ringCount;
    positions[centreIndex * 3 + 2] = 0;

    indices = new Uint32Array(triangles.length + ringCount * 3 + ringCount * 6);
    indices.set(triangles, 0);
    cursor = triangles.length;

    // Bottom fan, wound clockwise seen from +z so that its normal points down.
    for (let i = 0; i < ringCount; i += 1) {
      const a = bottomStart + i;
      const b = bottomStart + ((i + 1) % ringCount);
      indices[cursor++] = centreIndex;
      indices[cursor++] = b;
      indices[cursor++] = a;
    }
  }

  // Vertical skirt: one quad per boundary edge, outward facing. With a
  // channelled back the ring vertices are the boundary of the bottom surface,
  // so a channel reaching the edge simply makes the skirt shorter there —
  // which is exactly the notch the strip passes through into the next panel.
  for (let i = 0; i < ringCount; i += 1) {
    const next = (i + 1) % ringCount;
    const topA = boundary[i];
    const topB = boundary[next];
    const bottomA = bottomStart + (channel ? boundary[i] : i);
    const bottomB = bottomStart + (channel ? boundary[next] : next);

    indices[cursor++] = topA;
    indices[cursor++] = bottomA;
    indices[cursor++] = bottomB;

    indices[cursor++] = topA;
    indices[cursor++] = bottomB;
    indices[cursor++] = topB;
  }

  return {
    panel,
    mesh: { positions, indices },
    bounds: {
      min: [minX, minY, 0],
      max: [maxX, maxY, Number.isFinite(maxZ) ? maxZ : spec.baseThickness],
    },
  };
}

export interface BuildWallOptions {
  /** Overrides the design's mesh resolution, e.g. for a fast live preview. */
  segments?: number;
  /** Restricts generation to specific panel indices. */
  panelFilter?: (panel: PanelRef, index: number) => boolean;
  /** Invoked after each panel, for progress reporting. */
  onProgress?: (completed: number, total: number) => void;
}

/**
 * Builds every panel of a wall.
 *
 * The tessellation and the height field are created once and shared: all panels
 * have the same footprint, and the field is deliberately panel-agnostic, which
 * is both a large speed-up and the reason neighbouring panels agree at a joint.
 */
export function buildWallMesh(design: PanelDesign, options: BuildWallOptions = {}): WallMesh {
  const layout: WallLayout = layoutForDesign(design);
  const segments = Math.max(1, Math.round(options.segments ?? design.panel.segments));

  const tessellation = tessellateShape(layout.shape, design.panel.size, design.panel.gap, segments);
  const outline = layout.shape.outline(design.panel.size, design.panel.gap);
  const field = createHeightFieldForDesign(design, layout);

  // Sample spacing decides how steep a channel wall can be, because a height
  // field cannot make one steeper than a single step.
  const channel =
    design.led.mounting === 'channel'
      ? createChannelField({
          spec: design.led,
          wall: layout.metrics,
          baseThicknessMm: design.panel.baseThickness,
          outline,
          sampleSpacingMm: design.panel.size / Math.max(1, segments),
        })
      : null;

  const buildOptions: BuildPanelOptions = {
    shape: layout.shape,
    tessellation,
    outline,
    field,
    spec: design.panel,
    channel,
  };

  const selected = options.panelFilter
    ? layout.panels.filter((panel, index) => options.panelFilter!(panel, index))
    : layout.panels;

  const panels: PanelMesh[] = [];
  for (let i = 0; i < selected.length; i += 1) {
    panels.push(buildPanelMesh(selected[i], buildOptions));
    options.onProgress?.(i + 1, selected.length);
  }

  // Backlighting adds parts rather than changing the panels, so it runs after
  // them and cannot affect their geometry.
  const backlight = buildBacklight({
    design,
    panels: selected,
    outline,
    footprint: layout.footprint,
    wall: layout.metrics,
    panelMeshes: panels,
    channel,
  });

  return {
    panels,
    wall: layout.metrics,
    outline,
    frames: backlight.frames,
    strip: backlight.strip,
    backlight: backlight.report,
  };
}

/** Signed volume in mm³, via the divergence theorem over the triangles. */
export function meshVolume(mesh: TriangleMesh): number {
  const { positions, indices } = mesh;
  let volume = 0;

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i] * 3;
    const b = indices[i + 1] * 3;
    const c = indices[i + 2] * 3;

    const ax = positions[a];
    const ay = positions[a + 1];
    const az = positions[a + 2];
    const bx = positions[b];
    const by = positions[b + 1];
    const bz = positions[b + 2];
    const cx = positions[c];
    const cy = positions[c + 1];
    const cz = positions[c + 2];

    volume += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6;
  }

  return Math.abs(volume);
}

export interface MeshStats {
  panels: number;
  triangles: number;
  vertices: number;
  /** Total solid volume in cm³. */
  volumeCm3: number;
}

export function wallMeshStats(wall: WallMesh): MeshStats {
  let triangles = 0;
  let vertices = 0;
  let volume = 0;

  for (const panel of wall.panels) {
    triangles += panel.mesh.indices.length / 3;
    vertices += panel.mesh.positions.length / 3;
    volume += meshVolume(panel.mesh);
  }

  return {
    panels: wall.panels.length,
    triangles,
    vertices,
    volumeCm3: volume / 1000,
  };
}
